import React from "react";
import {
  PageBuilderSectionSliderFragment,
  PageBuilderSectionSliderSlideFragment
} from "types/graphql-types";
import PageBuilderSectionTitle from "./PageBuilderSectionTitle";
import { graphql } from "gatsby";
import { isPresent } from "src/utils/checks";
import SectionSlider, {
  SectionSliderItem
} from "components/SectionSlider/SectionSlider";
import {
  BackgroundColor,
  isSanityImagePresent
} from "@silversea-ssc/ui-components-react";
import {
  CruisePerDestinationCounts2Result,
  useCruisePerDestinationCounts
} from "components/FindYourCruise/useCruisePerDestinationCount";
import { cruisesLabel } from "src/utils/cruise";
import { toButtonVariantProps } from "./buttons";

type PageBuilderSectionSliderProps = PageBuilderSectionSliderFragment;

const PageBuilderSectionSlider: React.FC<
  React.PropsWithChildren<PageBuilderSectionSliderProps>
> = ({ heading, items = [], buttonLabel, buttonVariant }) => {
  const hasDestination = items.some(
    (item) => item?.page?.__typename == "SanityDestination"
  );

  const destinationsFacet = useCruisePerDestinationCounts(hasDestination);

  const filteredItems = items
    .map(toSectionSlider(destinationsFacet))
    .filter(isPresent);

  return (
    <React.Fragment>
      <PageBuilderSectionTitle {...heading} />
      <SectionSlider
        items={filteredItems}
        cardButtonLabel={buttonLabel?.localized}
        cardButtonVariant={toButtonVariantProps(buttonVariant).variant}
      />
    </React.Fragment>
  );
};

export default PageBuilderSectionSlider;

const toSectionSlider =
  (facetResults: CruisePerDestinationCounts2Result) =>
  (
    item: PageBuilderSectionSliderSlideFragment | undefined
  ): SectionSliderItem | undefined => {
    if (!item) return undefined;

    const page = item.page;
    const url = page?.fullPath;
    if (isPresent(page) && !isPresent(url)) return undefined;

    const card = page?.card;
    const image = item.image || card?.image;
    if (!isSanityImagePresent(image)) return undefined;

    const subtitle = buildSubtitle(item, facetResults);
    const destinationWebCode =
      page?.__typename == "SanityDestination"
        ? page.destinationWebCode
        : undefined;

    return {
      trackingName: card?.trackingName?.en,
      _key: item._key,
      title: card?.title?.localized || item.title?.localized,
      subtitle: subtitle,
      description: card?.description?.localized || item.description?.localized,
      image: image,
      url: url,
      destinationWebCode
    };
  };

const buildSubtitle = (
  item: PageBuilderSectionSliderSlideFragment,
  facetResults: CruisePerDestinationCounts2Result
): string | undefined => {
  const page = item.page;
  if (!page) return undefined;

  if (page.__typename == "SanityDestination" && page.destinationWebCode) {
    const { classic = 0, expedition = 0 } =
      facetResults[page.destinationWebCode] || {};
    return cruisesLabel(classic + expedition);
  }

  return undefined;
};

export const fragments = graphql`
  fragment PageBuilderSectionSliderSlide on SanitySectionSliderCard {
    _key
    page {
      __typename
      ... on SanityDestination {
        card {
          ...AsCard
        }
        destinationWebCode
        fullPath
      }

      ... on SanityPage {
        ...PageAsCard
      }

      ... on SanityLandingPage {
        ...LandingPageAsCard
      }
    }
    image {
      ...ImageWithAltAndPreview
    }
    title {
      localized
    }
    description {
      localized
    }
  }

  fragment PageBuilderSectionSlider on SanitySectionSlider {
    _key
    sectionLayoutConfig {
      ...SectionLayoutConfig
    }
    heading {
      ...SectionTitle
      ...SectionTitleCTAs
    }
    items {
      ...PageBuilderSectionSliderSlide
    }
    buttonVariant
    buttonLabel {
      localized
    }
  }
`;
